import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone, Sun, Moon } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const navLinks = [
  { label: 'Home', href: '#home' },
  { label: 'Practice Areas', href: '#practice-areas' },
  { label: 'About', href: '#about' },
  { label: 'Commitment', href: '#commitment' },
  { label: 'Contact', href: '#contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const { isDark, toggleTheme } = useTheme();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { threshold: 0.3 }
    );
    navLinks.forEach(link => {
      const el = document.getElementById(link.href.replace('#', ''));
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollTo = (href) => {
    setMobileOpen(false);
    const el = document.getElementById(href.replace('#', ''));
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <motion.nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'glass border-b py-3 shadow-lg'
          : 'bg-transparent py-5'
      }`}
      style={{ borderColor: scrolled ? 'var(--color-border)' : 'transparent' }}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        {/* Logo */}
        <a
          href="#home"
          onClick={(e) => { e.preventDefault(); scrollTo('#home'); }}
          data-testid="nav-logo"
          className="flex items-center gap-2"
        >
          <div className="w-10 h-10 rounded-md flex items-center justify-center font-heading font-bold text-lg"
            style={{ background: 'linear-gradient(135deg, var(--color-secondary), var(--color-accent))', color: '#fff' }}
          >
            F
          </div>
          <span className="font-heading font-bold text-lg tracking-tight hidden sm:inline" style={{ color: 'var(--color-text)' }}>
            Fitzgerald<span className="text-[var(--color-accent)]">&</span>Associates
          </span>
        </a>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-8">
          {navLinks.map(link => (
            <button
              key={link.href}
              data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, '-')}`}
              onClick={() => scrollTo(link.href)}
              className="relative font-body text-sm font-medium tracking-wide transition-colors duration-300"
              style={{ color: activeSection === link.href.replace('#', '') ? 'var(--color-accent)' : 'var(--color-text-muted)' }}
            >
              {link.label}
              {activeSection === link.href.replace('#', '') && (
                <motion.div
                  layoutId="activeNav"
                  className="absolute -bottom-1 left-0 right-0 h-0.5 rounded-full"
                  style={{ background: 'var(--color-accent)' }}
                />
              )}
            </button>
          ))}
        </div>

        {/* Right Actions */}
        <div className="flex items-center gap-3">
          <button
            data-testid="theme-toggle"
            onClick={toggleTheme}
            className="p-2 rounded-full transition-all duration-300 hover:scale-110"
            style={{ color: 'var(--color-text-muted)' }}
            aria-label="Toggle theme"
          >
            {isDark ? <Sun size={18} /> : <Moon size={18} />}
          </button>

          <a
            href="tel:+19176244469"
            data-testid="nav-phone"
            className="hidden md:flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold text-white transition-all duration-300 hover:shadow-lg"
            style={{ background: 'linear-gradient(135deg, var(--color-secondary), var(--color-accent))' }}
          >
            <Phone size={14} />
            (917) 624-4469
          </a>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden p-2"
            style={{ color: 'var(--color-text)' }}
            aria-label="Toggle mobile menu"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden overflow-hidden glass border-t"
            style={{ borderColor: 'var(--color-border)' }}
          >
            <div className="px-6 py-6 space-y-4">
              {navLinks.map(link => (
                <button
                  key={link.href}
                  data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                  onClick={() => scrollTo(link.href)}
                  className="block w-full text-left font-body text-base font-medium py-2 transition-colors"
                  style={{ color: activeSection === link.href.replace('#', '') ? 'var(--color-accent)' : 'var(--color-text)' }}
                >
                  {link.label}
                </button>
              ))}
              <a
                href="tel:+19176244469"
                data-testid="mobile-phone-cta"
                className="block w-full text-center py-3 rounded-md text-white font-semibold"
                style={{ background: 'linear-gradient(135deg, var(--color-secondary), var(--color-accent))' }}
              >
                Call (917) 624-4469
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}
